// ABOUTME: "None of these fits": up to a few windows the talent can do, entered as wall-clock times in the interview's
// ABOUTME: zone, kept inside the window pro-core allows, with the rows it refused marked and why (#484).
import { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { AppButton } from 'components/AppButton';
import { Text } from 'components/Text';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { MAX_PROPOSAL_ROWS } from '../constants';
import { formatInstant, localInputToIso } from '../format';
import {
  addMinutesToLocalInput,
  InvalidProposal,
  InvalidProposalReason,
  isoToLocalInput,
  isSameInstant,
  noticeHours
} from '../proposals';
import { InterviewTime, ProposalWindow } from '../types';

interface Row {
  start: string;
  end: string;
}

const EMPTY_ROW: Row = { start: '', end: '' };

const isEmpty = (row: Row) => !row.start && !row.end;
const isComplete = (row: Row) => !!row.start && !!row.end;

interface Props {
  timeZone: string;
  isSubmitting: boolean;
  onSubmit: (ranges: InterviewTime[]) => void;
  /** The window from the card read (#1907); without it the form asks nothing up front, as before. */
  window?: Partial<ProposalWindow> | null;
  /** The rows pro-core refused on the last send, each with its reason. */
  invalid?: InvalidProposal[];
  /** Now, for the notice in hours; injectable for tests. */
  now?: Date;
}

export const ProposeTimesForm = ({
  timeZone,
  isSubmitting,
  onSubmit,
  window,
  invalid = [],
  now = new Date()
}: Props) => {
  const [rows, setRows] = useState<Row[]>([EMPTY_ROW]);

  const maxRows =
    window?.maxProposals && window.maxProposals > 0
      ? window.maxProposals
      : MAX_PROPOSAL_ROWS;
  const min = window?.earliest
    ? isoToLocalInput(window.earliest, timeZone)
    : undefined;
  const max = window?.latest
    ? isoToLocalInput(window.latest, timeZone)
    : undefined;

  // The window the form itself enforces: a typed or pasted value is not held by the pickers' min and max, so a
  // complete row outside [earliest, latest] or shorter than the interview is marked and blocks submit here (#485),
  // before pro-core is asked. Without a window nothing is checked up front, as before.
  const clientReasonOf = (row: Row): InvalidProposalReason | null => {
    if (!window || !isComplete(row)) return null;
    const start = Date.parse(localInputToIso(row.start, timeZone));
    const end = Date.parse(localInputToIso(row.end, timeZone));
    if (window.earliest && start < Date.parse(window.earliest)) {
      return 'too-soon';
    }
    if (window.latest && start > Date.parse(window.latest)) return 'too-late';
    if (
      window.durationMinutes &&
      end - start < window.durationMinutes * 60_000
    ) {
      return 'too-short';
    }
    return null;
  };

  // Every row is either left empty (dropped) or complete with its end after its start: a half-filled row is never
  // dropped silently, so the talent never sends fewer windows than they think. A row the window refuses blocks submit
  // too; pro-core still decides.
  const filled = rows.filter(row => row.start && row.end);
  const isValid =
    filled.length > 0 &&
    rows.every(row => isEmpty(row) || isComplete(row)) &&
    filled.every(
      row =>
        localInputToIso(row.end, timeZone) >
          localInputToIso(row.start, timeZone) && !clientReasonOf(row)
    );

  const update = (index: number, field: keyof Row, value: string) =>
    setRows(current =>
      current.map((row, i) => {
        if (i !== index) return row;
        // A start with no end yet gets the interview's own length, the one choice the talent would otherwise type.
        if (field === 'start' && value && !row.end && window?.durationMinutes) {
          return {
            start: value,
            end: addMinutesToLocalInput(value, window.durationMinutes, timeZone)
          };
        }
        return { ...row, [field]: value };
      })
    );

  const reasonText = (reason: InvalidProposalReason): string => {
    if (reason === 'too-soon' && window?.earliest) {
      return getTransWithParams('interviews.propose.reason.tooSoon', {
        hours: String(noticeHours(window.earliest, now)),
        time: formatInstant(window.earliest, timeZone)
      });
    }
    if (reason === 'too-late' && window?.latest) {
      return getTransWithParams('interviews.propose.reason.tooLate', {
        time: formatInstant(window.latest, timeZone)
      });
    }
    if (reason === 'too-short' && window?.durationMinutes) {
      return getTransWithParams('interviews.propose.reason.tooShort', {
        minutes: window.durationMinutes
      });
    }
    return getTrans('interviews.propose.reason.unknown');
  };

  /** The form's own check first, then what pro-core refused on the last send for this very start. */
  const reasonOf = (row: Row): string | null => {
    const own = clientReasonOf(row);
    if (own) return reasonText(own);
    if (!row.start) return null;
    const refused = invalid.find(item =>
      isSameInstant(item.startsAt, localInputToIso(row.start, timeZone))
    );
    return refused ? reasonText(refused.reason) : null;
  };

  /** The end's own minimum: the interview length after the start, so the picker offers no shorter window. */
  const endMin = (row: Row): string | undefined => {
    if (!row.start) return min;
    return window?.durationMinutes
      ? addMinutesToLocalInput(row.start, window.durationMinutes, timeZone)
      : row.start;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Text variant="body2">
        {getTrans('interviews.propose.hint')} ({timeZone})
      </Text>
      {rows.map((row, index) => {
        const reason = reasonOf(row);
        return (
          <Box
            key={index}
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                type="datetime-local"
                size="small"
                fullWidth
                label={getTrans('interviews.propose.from')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min, max }}
                error={!!reason}
                value={row.start}
                onChange={event => update(index, 'start', event.target.value)}
              />
              <TextField
                type="datetime-local"
                size="small"
                fullWidth
                label={getTrans('interviews.propose.to')}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: endMin(row) }}
                error={!!reason}
                value={row.end}
                onChange={event => update(index, 'end', event.target.value)}
              />
            </Box>
            {!isEmpty(row) && !isComplete(row) && (
              <Text
                variant="caption"
                color="error"
                data-testid="interview-propose-incomplete"
              >
                {getTrans('interviews.propose.incomplete')}
              </Text>
            )}
            {reason && (
              <Text
                variant="caption"
                color="error"
                data-testid="interview-propose-row-invalid"
              >
                {reason}
              </Text>
            )}
          </Box>
        );
      })}
      {rows.length < maxRows && (
        <AppButton
          variant="text"
          testId="interview-propose-add"
          onClick={() => setRows(current => [...current, EMPTY_ROW])}
        >
          {getTrans('interviews.propose.addRow')}
        </AppButton>
      )}
      <AppButton
        variant="outlined"
        testId="interview-propose-submit"
        disabled={!isValid || isSubmitting}
        onClick={() =>
          onSubmit(
            filled.map(row => ({
              startsAt: localInputToIso(row.start, timeZone),
              endsAt: localInputToIso(row.end, timeZone)
            }))
          )
        }
      >
        {getTrans('interviews.propose.submit')}
      </AppButton>
    </Box>
  );
};
